import React, {useCallback, useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileExport, faFilter, faSearch} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";
import usePrevious from "../../hooks/usePrevious.js";

// Components
import BaseTable from "../../components/BaseTable.js";
import PhoneNumbers from "../../components/PhoneNumbers.js";
import Badge from "../../components/Badge.js";
import Modal from "../../components/Modal.js";
import ModalUserFilter from "./ModalUserFilter.js";
import ModalExport from "../../components/ModalExport.js";

// Style
import {colors, heroTheme, pad} from "../../style/components/variables.js";
import {bp} from "../../style/components/breakpoints.js";
import {
  Abbr,
  Inline,
  Text,
  TableFooter,
  TableWrapper,
  Small,
  SearchWrapper,
  Search,
  Button,
  Heading,
  TableMenu,
  SearchIcon,
  Pill
} from "../../style/components/general.js";

const ModalViewAllFacilityUsers = ({visible, setVisible}) => {
  const isMounted = useMountedState();

  const {atLeast, roleCanAccessResource, roles} = useContext(AuthContext);
  const {facility} = useContext(FacilityNavContext);

  const [tableData, setTableData] = useState(null);
  const [query, setQuery] = useState(null);
  const [orderBy, setOrderBy] = useState("desc");
  const [groupBy, setGroupBy] = useState("createdAt");
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pages, setPages] = useState(1);
  const [activeFilters, setActiveFilters] = useState();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showModalFilters, setShowModalFilters] = useState(false);
  const [showModalExport, setShowModalExport] = useState(false);

  const scrollPos = useRef(null);
  const scrollContainer = useRef(null);

  const {api, loading} = useApi("facility-users");

  const limit = useRef(12);

  const prevOrderBy = usePrevious(orderBy);
  const prevGroupBy = usePrevious(groupBy);

  const formatTableData = useCallback(
    formattedUsers =>
      formattedUsers.map(user => {
        const {
          publicId,
          type,
          role,
          firstName,
          lastName,
          isInvited,
          isAccepted,
          isDeleted,
          email,
          primary,
          phones,
          company
        } = user;

        return {
          publicId: publicId,
          firstName: `${firstName} ${lastName}`,
          userCompanyId: company?.name,
          userRoleId: role?.label || type?.name?.toUpperCase(),
          status: (
            <>
              {isInvited && isAccepted && !isDeleted && (
                <Pill color={heroTheme.success}>
                  <Small inverted>Active</Small>
                </Pill>
              )}
              {isDeleted && (
                <Pill color={colors.gray}>
                  <Small inverted>
                    <Abbr title="User is deleted">deleted!</Abbr>
                  </Small>
                </Pill>
              )}
              {isInvited && !isAccepted && !isDeleted && (
                <Pill color={heroTheme.warning}>
                  <Small inverted>
                    <Abbr title="User has not accepted invitation">invited</Abbr>
                  </Small>
                </Pill>
              )}
              {!isInvited && !isDeleted && (
                <Pill color={colors.gray}>
                  <Small inverted>
                    <Abbr title="User has not been invited">contact</Abbr>
                  </Small>
                </Pill>
              )}
            </>
          ),
          email: <a href={`mailto:${email}`}>{email}</a>,
          phone: primary && <PhoneNumbers primary={primary} phones={phones} />,
          isDeleted
        };
      }),
    []
  );

  const getTableData = useCallback(
    (page, l) => {
      const filter = {
        ExcludeCreator: true
      };

      if (query) filter.Search = query;
      if (activeFilters?.roles) filter.Roles = activeFilters.roles;
      if (showDeleted) filter.showDeleted = true;

      const payload = {
        page: page || 1,
        orderBy: orderBy,
        groupBy,
        limit: l ?? limit.current,
        filter: JSON.stringify(filter),
        userManagement: true
      };

      if (facility?.id) payload.facilityId = facility.id;

      api.callGet(null, payload).then(({status, data}) => {
        if (status === 200 && data?.users) {
          const formatted = formatTableData(data.users);

          // Page Limit
          const {total: dataTotal, page: tPage, pages: tPages} = data;

          setCurrent(tPage);
          setPages(tPages);
          setTableData(prev => (page === 1 ? formatted : [...prev, ...formatted]));
          setTotal(dataTotal);
        }
      });
    },
    [api, formatTableData, facility, query, orderBy, groupBy, showDeleted, activeFilters]
  );

  useEffect(() => {
    if (
      isMounted() &&
      prevOrderBy &&
      prevGroupBy &&
      (prevOrderBy !== orderBy || prevGroupBy !== groupBy)
    )
      getTableData(1);
  }, [isMounted, getTableData, prevOrderBy, prevGroupBy, orderBy, groupBy]);

  useEffect(() => {
    if (query || query === "") getTableData(1);
  }, [getTableData, query]);

  // Initial Load
  useEffect(() => {
    if (isMounted() && !tableData) {
      // estimation for number of cards to overflow
      const approxRows = Math.floor((window.innerHeight * 0.9 - 200) / 40);
      limit.current = approxRows;
      getTableData(1, approxRows);
    }
  }, [isMounted, tableData, getTableData]);

  // Load more on Scroll
  useEffect(() => {
    const modal = document.getElementById("modal");

    const handleScroll = () => {
      const maxScrollTop = modal.scrollHeight - modal.clientHeight;
      if (modal.scrollTop + 1 >= maxScrollTop && current < pages && !loading)
        getTableData(current + 1);
    };

    modal.addEventListener("scroll", handleScroll);

    return () => modal.removeEventListener("scroll", handleScroll);
  }, [current, pages, getTableData, loading]);

  useLayoutEffect(() => {
    if (showModalFilters || showModalExport) {
      scrollPos.current = document.getElementById("modal").scrollTop;
      setShowModal(true);
    } else {
      scrollContainer.current.scrollTop = scrollPos.current;
      setShowModal(false);
      setItems([]);
    }
  }, [showModalFilters, showModalExport]);

  const handleSearch = e => {
    setCurrent(1);
    setPages(1);
    setQuery(e.target.value);
  };

  if (showModal && showModalFilters)
    return (
      <ModalUserFilter
        visible={showModalFilters}
        setVisible={setShowModalFilters}
        hasBackButton
        goBack={() => setShowModalFilters(false)}
        filters={activeFilters}
        setFilters={setActiveFilters}
        showDeleted={showDeleted}
        setShowDeleted={setShowDeleted}
      />
    );

  if (showModal && showModalExport)
    return (
      <ModalExport
        visible={showModalExport}
        setVisible={setShowModalExport}
        getExport={async exportParams =>
          api.callGet(null, {
            ...exportParams,
            facilityId: facility.id,
            filter: JSON.stringify({ExcludeCreator: true})
          })
        }
        hasBackButton
        goBack={() => setShowModalExport(false)}
      />
    );

  return (
    <Modal ref={scrollContainer} visible={visible} setVisible={setVisible}>
      <TableWrapper>
        <TableMenuRow>
          <StyledInline>
            <Heading>Users</Heading>
            <InlineSpaced>
              <SearchWrapper>
                <SearchIcon>
                  <FontAwesomeIcon icon={faSearch} />
                </SearchIcon>
                <Search
                  name="search"
                  type="text"
                  placeholder="Search..."
                  onChange={e => handleSearch(e)}
                />
              </SearchWrapper>
              <TableMenu>
                {atLeast("admin") &&
                  roles?.length > 0 &&
                  (tableData?.length > 0 ||
                    (activeFilters && Object.keys(activeFilters)?.length > 0)) && (
                    <Option
                      type="button"
                      onClick={() => {
                        setShowModalFilters(!showModalFilters);
                      }}>
                      <Badge
                        count={
                          (activeFilters?.roles ? activeFilters.roles.length : 0) +
                          (showDeleted ? 1 : 0)
                        }
                        title="Active filters"
                        offset="14px"
                        color={colors.heroGreen}
                      />
                      <FontAwesomeIcon icon={faFilter} />
                    </Option>
                  )}
                {roleCanAccessResource("user", "export") && tableData?.length > 0 && (
                  <Option
                    type="button"
                    onClick={() => {
                      setShowModalExport(true);
                    }}>
                    <abbr title="Export">
                      <FontAwesomeIcon icon={faFileExport} />
                    </abbr>
                  </Option>
                )}
              </TableMenu>
            </InlineSpaced>
          </StyledInline>
        </TableMenuRow>
        <BaseTable
          headings={{
            publicId: {header: "", disabled: true},
            firstName: {header: "Name", disabled: false},
            userCompanyId: {header: "Company", disabled: false},
            userRoleId: {header: "Role", disabled: false},
            status: {header: "Status", disabled: false},
            email: {header: "Email", disabled: false},
            phone: {header: "Phone", disabled: true}
          }}
          data={tableData}
          loading={loading}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          items={items}
          setItems={setItems}
        />
        <TableFooter>
          <Inline>
            <Text>
              <span>{total} total</span>
            </Text>
          </Inline>
        </TableFooter>
      </TableWrapper>
    </Modal>
  );
};

ModalViewAllFacilityUsers.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired
};

// Style Overrides
const TableMenuRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${pad}px;
`;

const Option = styled(Button)`
  margin-left: ${pad}px;
`;

const StyledInline = styled(Inline)`
  gap: ${pad}px;
  display: block;
  width: 100%;

  ${bp(1)} {
    display: flex;
  }
`;

const InlineSpaced = styled(Inline)`
  justify-content: space-between;
  width: 100%;
`;

export default ModalViewAllFacilityUsers;
